import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, BookOpen, ChevronDown, Clock, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import PublicLayout from '../../components/public/PublicLayout';
import SEO from '../../components/SEO';
import { PUBLIC_BASE } from './config';

const FALLBACK_IMG =
  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg';

function getTimeAgo(dateStr) {
  if (!dateStr) return '';
  const created = new Date(dateStr);
  const now = new Date();
  const diffMs = now - created;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return '오늘';
  if (diffDays === 1) return '어제';
  if (diffDays < 7) return `${diffDays}일 전`;
  const m = created.getMonth() + 1;
  const d = created.getDate();
  return `${m}월 ${d}일`;
}

function HotPlaceCard({ item }) {
  const navigate = useNavigate();

  return (
    <div
      onClick={() => navigate(`${PUBLIC_BASE}/magazine/hot-place/${item.id}`)}
      className="group flex flex-col sm:flex-row bg-[#182334] rounded-2xl overflow-hidden border border-white/5 cursor-pointer hover:border-white/15 transition-all hover:shadow-xl hover:shadow-black/20">
      <div className="relative sm:w-[45%] shrink-0">
        <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
          <img
            src={item.thumbnail || FALLBACK_IMG}
            alt={item.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors hidden sm:block" />
        </div>
      </div>
      <div className="flex-1 p-4 sm:p-5 flex flex-col justify-between min-w-0">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-block text-[11px] font-medium px-2.5 py-1 rounded-full bg-rose-500/15 text-rose-400">
              핫플힙플
            </span>
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white/[0.06] text-slate-400">No.{item.episodeNo}</span>
          </div>
          <h3 className="text-white font-bold text-base sm:text-lg mb-2 leading-snug line-clamp-2 group-hover:text-sky-300 transition-colors">
            {item.title}
          </h3>
          {item.summary && (
            <p className="text-slate-400 text-xs sm:text-sm leading-relaxed line-clamp-3 mb-3">
              {item.summary}
            </p>
          )}
        </div>
        <div className="flex items-center justify-between mt-auto">
          <span className="flex items-center gap-1 text-slate-600 text-[11px]">
            <Clock size={11} />
            {getTimeAgo(item.createdAt)}
          </span>
          <span className="text-slate-500 group-hover:text-sky-400 text-xs transition-colors">
            자세히 보기 →
          </span>
        </div>
      </div>
    </div>
  );
}

function PublicHotPlacePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchItems = useCallback((offset = 0, limit = 4) => {
    return axios.get('/api/genie-magazine', { params: { offset, limit } }).then((res) => res.data);
  }, []);

  useEffect(() => {
    fetchItems()
      .then((data) => {
        setItems(data.items || []);
        setHasMore(data.hasMore || false);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [fetchItems]);

  const handleLoadMore = () => {
    setLoadingMore(true);
    fetchItems(items.length, 4)
      .then((data) => {
        setItems((prev) => [...prev, ...(data.items || [])]);
        setHasMore(data.hasMore || false);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  return (
    <PublicLayout>
      <SEO
        title="핫플힙플 - 매거진"
        description="지니 매거진 핫플힙플 콘텐츠를 만나보세요."
        path="/magazine/hot-place"
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-10">
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight text-center">매거진</h1>
          <p className="text-center text-slate-500 text-sm mt-1">음악 트렌드와 문화 이야기</p>
          {/* 탭 */}
          <div className="flex items-center justify-center gap-2 mt-4">
            <Link
              to={`${PUBLIC_BASE}/magazine/music-trends`}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors bg-white/[0.04] text-slate-400 border border-white/[0.08] hover:bg-white/[0.08]">
              <TrendingUp size={14} />
              뮤직트렌드
            </Link>
            <Link
              to={`${PUBLIC_BASE}/magazine/hot-place`}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors bg-sky-500/20 text-sky-400 border border-sky-500/30">
              <BookOpen size={14} />
              핫플힙플
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 gap-3 text-slate-400">
            <Loader2 size={20} className="animate-spin" />
            <span>로딩 중...</span>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-500">
            <BookOpen size={40} />
            <p>콘텐츠가 없습니다.</p>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4">
              {items.map((item) => (
                <HotPlaceCard key={item.id} item={item} />
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="flex items-center gap-2 px-6 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-slate-300 text-sm transition-colors disabled:opacity-50">
                  {loadingMore ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <ChevronDown size={14} />
                  )}
                  더보기
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </PublicLayout>
  );
}

export default PublicHotPlacePage;
